import React, { useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { Table, Pagination, Spin, Empty } from 'antd';
import PageLayout from '@components/layout/PageLayout';
import { useAuditData } from '@hooks/graphql/useAuditData';
import { useAuditClientFilter } from '@hooks/useAuditClientFilter';
import AuditFilterPanel, { AuditFilters, getDateRange } from './AuditFilterPanel';
import { buildAuditColumns, ExpandedRow } from './auditTableConfig';
import styles from './Audit.module.css';

const initialDateRange = getDateRange('thisMonth', null);

const Audit = () => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<AuditFilters>({
    ...initialDateRange,
    meterId: null,
  });

  const { data, totalCount, loading, currentPage, pageSize, onChangePage } = useAuditData(
    undefined,
    undefined,
    filters.startDate || undefined,
    filters.endDate || undefined,
    filters.meterId ?? null,
    filters.suppressQuery,
  );

  const filteredData = useAuditClientFilter(data, filters.actorFilter ?? '', filters.windFarmFilter ?? '');

  const toggleExpand = (id: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const columns = buildAuditColumns(expandedKeys, toggleExpand);

  const emptyDescription = useMemo(() => {
    if (filters.meterId) {
      return (
        <span>
          No audit activity found for this meter in the selected time frame.
          <br />
          Try a wider range — e.g. switch from Last 7 Days to This Month or a Custom Range.
        </span>
      );
    }
    return 'No audit events';
  }, [filters.meterId]);

  return (
    <PageLayout title="Audit Trail">
      <AuditFilterPanel recordCount={filteredData.length} onChange={setFilters} />
      <div className={styles.tableWrapper}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{ emptyText: <Empty description={emptyDescription} /> }}
            expandable={{
              expandedRowKeys: Array.from(expandedKeys),
              expandedRowRender: (record) => <ExpandedRow record={record} />,
              rowExpandable: (record) => record.before !== null || record.after !== null,
              showExpandColumn: false,
            }}
          />
        </Spin>

        {totalCount > 0 && (
          <div className={styles.paginationBar}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={onChangePage}
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showTotal={(total) => `${total} records`}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Audit;
